import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileShopService } from '../../api/generated/services/MobileShopService';
import type { JtlOrderView } from '../../api/generated/models/JtlOrderView';
import type { ShopifyOrderView } from '../../api/generated/models/ShopifyOrderView';
import type { MoneyView } from '../../api/generated/models/MoneyView';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';

type UnifiedOrderView = JtlOrderView | ShopifyOrderView;

interface OrderDetailSidebarProps {
  shopId: string;
  orderId: number;
  onClose: () => void;
}

function CopyableValue({ text }: { text: string }) {
  const [highlighted, setHighlighted] = React.useState(false);

  const handleCopy = React.useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setHighlighted(true);
      setTimeout(() => setHighlighted(false), 600);
    });
  }, [text]);

  return (
    <span className={`copyable-value ${highlighted ? 'copyable-value--flash' : ''}`}>
      <span className="copyable-value-text">{text}</span>
      <button type="button" className="copyable-value-btn" onClick={handleCopy} aria-label="Copy" title="Copy">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      </button>
    </span>
  );
}

/**
 * ТЗ п.8: Slide-out sidebar with full order details.
 * Handles both JtlOrderView and ShopifyOrderView - both share the same field structure.
 *
 * Note: getAppMobileShopOrder is typed as OrderView in generated service,
 * but actual API returns JtlOrderView or ShopifyOrderView depending on shop type.
 */
export const OrderDetailSidebar: React.FC<OrderDetailSidebarProps> = ({ shopId, orderId, onClose }) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState<UnifiedOrderView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    MobileShopService.getAppMobileShopOrder(shopId, orderId)
      .then((data) => setOrder(data as unknown as UnifiedOrderView))
      .catch((err) => {
        setOrder(null);
        setError(err instanceof ApiError ? (err.body?.message ?? err.message) : String(err));
      })
      .finally(() => setLoading(false));
  }, [shopId, orderId]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const formatPrice = useCallback((money: MoneyView | null | undefined): string => {
    if (money?.amount == null) return '-';
    return helper.formatPrice({ amount: Math.round(money.amount * 100), currency: money.currencyIso ?? 'EUR' });
  }, []);

  return (
    <>
      <div className="order-sidebar-overlay" onClick={onClose} />

      <div className="order-sidebar">
        <div className="order-sidebar-header">
          <h2 className="order-sidebar-title">
            {t('dashboard_order_detail_title', { number: order?.orderNumber ?? orderId })}
          </h2>
          <button type="button" className="order-sidebar-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="order-sidebar-content">
          {loading && (
            <div className="order-sidebar-loader">
              <div className="order-sidebar-spinner" />
              <span>{t('loading_text')}</span>
            </div>
          )}

          {error && (
            <p className="dashboard-error-inline">{error}</p>
          )}

          {!loading && !error && order && (
            <>
              {/* Items */}
              {order.items && order.items.length > 0 && (
                <div className="order-sidebar-section">
                  {order.items.map((item, idx) => (
                    <div key={item.productId ?? idx} className="order-sidebar-product">
                      <div className="order-sidebar-product-image-wrapper">
                        {item.thumbnail ? (
                          <img src={item.thumbnail} alt={item.name ?? ''} className="order-sidebar-product-image" />
                        ) : (
                          <div className="order-sidebar-product-image order-sidebar-product-placeholder">-</div>
                        )}
                      </div>
                      <div className="order-sidebar-product-info">
                        <div className="order-sidebar-product-name">{item.name}</div>
                        {item.articleNumber && (
                          <div className="order-sidebar-product-meta order-sidebar-product-artnr">
                            {item.articleNumber}
                          </div>
                        )}
                        <div className="order-sidebar-product-meta">
                          {t('dashboard_order_qty')}: {item.quantity}
                        </div>
                        <div className="order-sidebar-product-meta">
                          {t('dashboard_order_price')}: {formatPrice(item.priceGross)}
                        </div>
                        {'totalPriceGross' in item && item.totalPriceGross && item.quantity && item.quantity > 1 && (
                          <div className="order-sidebar-product-meta">
                            {t('dashboard_order_total')}: {formatPrice(item.totalPriceGross as MoneyView)}
                          </div>
                        )}
                        {item.vat != null && (
                          <div className="order-sidebar-product-meta">
                            VAT: {item.vat}%
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Order info */}
              <div className="order-sidebar-section">
                <table className="order-sidebar-table">
                  <tbody>
                    <tr>
                      <td className="order-sidebar-table-label">{t('dashboard_order_id')}:</td>
                      <td><CopyableValue text={String(order.id)} /></td>
                    </tr>
                    {order.orderNumber && (
                      <tr>
                        <td className="order-sidebar-table-label">{t('dashboard_order_number')}:</td>
                        <td><CopyableValue text={order.orderNumber} /></td>
                      </tr>
                    )}
                    {order.orderDate && (
                      <tr>
                        <td className="order-sidebar-table-label">{t('dashboard_order_created')}:</td>
                        <td>{helper.formatDate(order.orderDate)}</td>
                      </tr>
                    )}
                    {order.status && (
                      <tr>
                        <td className="order-sidebar-table-label">{t('dashboard_order_status')}:</td>
                        <td className="order-sidebar-status">{order.status}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals + payment + shipping */}
              <div className="order-sidebar-section">
                <table className="order-sidebar-table">
                  <tbody>
                    <tr>
                      <td className="order-sidebar-table-label">{t('dashboard_order_total')}:</td>
                      <td className="order-sidebar-total">
                        {formatPrice(order.totalPriceNet)} / {formatPrice(order.totalPriceGross)}
                      </td>
                    </tr>
                    {order.billingMethodName && (
                      <tr>
                        <td className="order-sidebar-table-label">{t('dashboard_order_billing')}:</td>
                        <td>{order.billingMethodName}</td>
                      </tr>
                    )}
                    {order.shippingMethodName && (
                      <tr>
                        <td className="order-sidebar-table-label">{t('dashboard_order_shipping')}:</td>
                        <td>{order.shippingMethodName}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Customer info */}
              {(order.customerName || order.customerEmail || ('customerPhone' in order && order.customerPhone)) && (
                <div className="order-sidebar-section">
                  <table className="order-sidebar-table">
                    <tbody>
                      {order.customerName && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_customer_name')}:</td>
                          <td>{order.customerName}</td>
                        </tr>
                      )}
                      {order.customerEmail && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_customer_email')}:</td>
                          <td>
                            <span className="copyable-contact">
                              <a href={`mailto:${order.customerEmail}`} className="order-sidebar-contact-link">{order.customerEmail}</a>
                              <CopyableValue text={order.customerEmail} />
                            </span>
                          </td>
                        </tr>
                      )}
                      {'customerPhone' in order && order.customerPhone && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_customer_phone')}:</td>
                          <td>
                            <span className="copyable-contact">
                              <a href={`tel:${order.customerPhone}`} className="order-sidebar-contact-link">{order.customerPhone}</a>
                              <CopyableValue text={order.customerPhone} />
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Billing address */}
              {order.billingAddress && (
                <div className="order-sidebar-section">
                  <table className="order-sidebar-table">
                    <tbody>
                      {order.billingAddress.street && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_street')}:</td>
                          <td>{order.billingAddress.street}</td>
                        </tr>
                      )}
                      {order.billingAddress.city && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_city')}:</td>
                          <td>{order.billingAddress.city}</td>
                        </tr>
                      )}
                      {order.billingAddress.postCode && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_postcode')}:</td>
                          <td>{order.billingAddress.postCode}</td>
                        </tr>
                      )}
                      {order.billingAddress.country && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_country')}:</td>
                          <td>{order.billingAddress.country}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Shipping address */}
              {order.shippingAddress && (
                <div className="order-sidebar-section">
                  <table className="order-sidebar-table">
                    <tbody>
                      <tr>
                        <td className="order-sidebar-table-label" colSpan={2}><strong>{t('dashboard_order_shipping_address')}</strong></td>
                      </tr>
                      {order.shippingAddress.street && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_street')}:</td>
                          <td>{order.shippingAddress.street}</td>
                        </tr>
                      )}
                      {order.shippingAddress.city && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_city')}:</td>
                          <td>{order.shippingAddress.city}</td>
                        </tr>
                      )}
                      {order.shippingAddress.postCode && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_postcode')}:</td>
                          <td>{order.shippingAddress.postCode}</td>
                        </tr>
                      )}
                      {order.shippingAddress.country && (
                        <tr>
                          <td className="order-sidebar-table-label">{t('dashboard_order_country')}:</td>
                          <td>{order.shippingAddress.country}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Comment */}
              {order.comment && (
                <div className="order-sidebar-section">
                  <div className="order-sidebar-comment-label">{t('dashboard_order_comment')}:</div>
                  <div className="order-sidebar-comment">{order.comment}</div>
                </div>
              )}

              {/* Tracking */}
              {order.tracking && (
                <div className="order-sidebar-section">
                  <table className="order-sidebar-table">
                    <tbody>
                      <tr>
                        <td className="order-sidebar-table-label">{t('dashboard_order_tracking')}:</td>
                        <td><CopyableValue text={order.tracking} /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
